"use client";

import Link from "@/lib/router/link";
import { usePathname, useRouter } from "@/lib/router/navigation";
import { Building2, LogOut, ShieldHalf, UserRound, UserCheck } from "lucide-react";
import { useSessionCapabilities } from "@/lib/hooks/use-session-capabilities";
import { useTenancy } from "@/lib/tenancy/tenancy-context";
import { useOptionalAuth } from "@/lib/auth/auth-context";
import { isOidcConfigured } from "@/lib/auth/oidc";
import { belongsToNoOrganisation, claimsDisplayName, initialsOf } from "@/lib/auth/organisation";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function UserMenu() {
  const { user } = useTenancy();
  const router = useRouter();
  const auth = useOptionalAuth();
  // Sign out ENDS the session: the tab's tokens and the identity provider's
  // session (auth-context signOut). It used to only navigate to /login, which
  // left the session alive, so the next person at the machine was still signed
  // in. Without an identity provider (mock mode) there is no session to end.
  const signOut = () => (auth && isOidcConfigured() ? auth.signOut() : router.push("/login"));
  // 3.143: an account with no organisation has no tenant session, so its name
  // comes from its token (name, username or email); it also gets no tenant links.
  const noOrganisation = auth ? belongsToNoOrganisation(auth.status, auth.claims) : false;
  const tokenName = claimsDisplayName(auth?.claims);
  const caps = useSessionCapabilities().data;
  const onPlatform = usePathname().startsWith("/platform");
  const name = user ? `${user.firstName} ${user.lastName}` : tokenName;
  const initials = user ? `${user.firstName[0] ?? ""}${user.lastName[0] ?? ""}` : tokenName ? initialsOf(tokenName) : "?";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <Avatar className="size-8">
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{initials.toUpperCase()}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-medium" data-testid="user-menu-name">{name ?? "—"}</span>
            <span className="text-xs text-muted-foreground">{user?.email ?? (auth?.claims?.email !== tokenName ? auth?.claims?.email : undefined)}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Also none for an operator whose organisation does not resolve (dev-user, the gate fixture). */}
        {!noOrganisation && !(caps?.platformOperator && !caps.organisationId) && (
          <>
            <DropdownMenuItem asChild><Link href="/my-permissions"><UserCheck className="size-4" /> My permissions</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link href="/members"><UserRound className="size-4" /> Members</Link></DropdownMenuItem>
          </>
        )}
        {/* Console wording ruling (2026-10-08): no "Account settings" entry; that
            page's fields save nothing. The /account route is unchanged. */}
        {/* CROSSING (the sponsor, 2026-10-14; the coordinator's design). 2.968
            removed a "Platform admin" link because the console could not tell an
            operator from a tenant user; the platform now says so itself (GET
            /v1/session/capabilities), so the link returns for operators ONLY. An
            operator in the tenant shell gets "Platform"; an operator in the
            platform shell gets "Organisation console" only when an organisation
            RESOLVES (organisation_id present), never from the token's org_id,
            which the gate's fixture organisation sets with no rows behind it.
            A non-operator never sees a platform link. */}
        {caps?.platformOperator && !onPlatform && (
          <DropdownMenuItem asChild><Link href="/platform"><ShieldHalf className="size-4" /> Platform</Link></DropdownMenuItem>
        )}
        {caps?.platformOperator && onPlatform && caps.organisationId && (
          <DropdownMenuItem asChild><Link href="/overview"><Building2 className="size-4" /> Organisation console</Link></DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
          <LogOut className="size-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
